import { OVERBLEED, QUAD } from './constants';
import { bindPointerEvents } from './events';
import { createPoints, stepMotion } from './motion';
import { DOWN_FRAG, MESH_FRAG, PRESENT_FRAG, UP_FRAG, VERT } from './shaders';
import { createTarget, destroyTarget, linkProgram } from './webglCore';

export const startWebGL = (canvas, mouseRef, scrollRef, paletteRef, animationRef) => {
  const gl = canvas.getContext('webgl2', {
    alpha: true,
    antialias: false,
    premultipliedAlpha: false,
    preserveDrawingBuffer: false,
  });
  if (!gl) return null;
  if (!gl.getExtension('EXT_color_buffer_float')) return () => {};

  const meshProg = linkProgram(gl, VERT, MESH_FRAG);
  const downProg = linkProgram(gl, VERT, DOWN_FRAG);
  const upProg = linkProgram(gl, VERT, UP_FRAG);
  const presentProg = linkProgram(gl, VERT, PRESENT_FRAG);
  if (!meshProg || !downProg || !upProg || !presentProg) return () => {};

  const vao = gl.createVertexArray();
  const buffer = gl.createBuffer();
  gl.bindVertexArray(vao);
  gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
  gl.bufferData(gl.ARRAY_BUFFER, QUAD, gl.STATIC_DRAW);
  const bindAttrib = (program) => {
    const loc = gl.getAttribLocation(program, 'a_position');
    gl.enableVertexAttribArray(loc);
    gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);
  };
  bindAttrib(meshProg);
  bindAttrib(downProg);
  bindAttrib(upProg);
  bindAttrib(presentProg);
  gl.bindVertexArray(null);

  const loc = {
    meshRes: gl.getUniformLocation(meshProg, 'u_resolution'),
    meshPts: gl.getUniformLocation(meshProg, 'u_points'),
    meshCols: gl.getUniformLocation(meshProg, 'u_colors'),
    meshRad: gl.getUniformLocation(meshProg, 'u_radii'),
    downTex: gl.getUniformLocation(downProg, 'u_tex'),
    downOff: gl.getUniformLocation(downProg, 'u_offset'),
    upTex: gl.getUniformLocation(upProg, 'u_tex'),
    upOff: gl.getUniformLocation(upProg, 'u_offset'),
    presentTex: gl.getUniformLocation(presentProg, 'u_tex'),
    presentOff: gl.getUniformLocation(presentProg, 'u_offset'),
  };

  let fullWidth = 0;
  let fullHeight = 0;
  let pendingResize = false;
  let levels = [];
  const points = createPoints();

  const disposeLevels = () => {
    levels.forEach((level) => destroyTarget(gl, level));
    levels = [];
  };

  const applySize = () => {
    fullWidth = window.innerWidth + OVERBLEED;
    fullHeight = window.innerHeight + OVERBLEED;
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    canvas.style.width = `${fullWidth}px`;
    canvas.style.height = `${fullHeight}px`;
    canvas.width = Math.round(fullWidth * dpr);
    canvas.height = Math.round(fullHeight * dpr);

    disposeLevels();
    let w = Math.max(1, Math.round(canvas.width * 0.5));
    let h = Math.max(1, Math.round(canvas.height * 0.5));
    for (let i = 0; i < 4; i++) {
      const target = createTarget(gl, w, h);
      if (!target) {
        disposeLevels();
        return false;
      }
      levels.push(target);
      w = Math.max(1, Math.round(w * 0.5));
      h = Math.max(1, Math.round(h * 0.5));
    }
    return true;
  };

  if (!applySize()) return () => {};

  const unbind = bindPointerEvents(mouseRef, scrollRef, () => ({ fullWidth, fullHeight }));

  const drawQuad = (program, target, src, offsetLoc, texLoc) => {
    gl.bindFramebuffer(gl.FRAMEBUFFER, target ? target.fbo : null);
    gl.viewport(0, 0, target ? target.w : canvas.width, target ? target.h : canvas.height);
    gl.useProgram(program);
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, src.tex);
    gl.uniform1i(texLoc, 0);
    gl.uniform2f(offsetLoc, 0.5 / src.w, 0.5 / src.h);
    gl.bindVertexArray(vao);
    gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
  };

  const animate = () => {
    if (pendingResize) {
      pendingResize = false;
      if (!applySize()) {
        animationRef.current = requestAnimationFrame(animate);
        return;
      }
    }

    const { positions, colors, radii } = stepMotion(
      points,
      mouseRef.current,
      scrollRef.current,
      fullHeight,
      paletteRef.current
    );

    const mesh = levels[0];
    gl.bindFramebuffer(gl.FRAMEBUFFER, mesh.fbo);
    gl.viewport(0, 0, mesh.w, mesh.h);
    gl.clearColor(0, 0, 0, 0);
    gl.clear(gl.COLOR_BUFFER_BIT);
    gl.useProgram(meshProg);
    gl.uniform2f(loc.meshRes, mesh.w, mesh.h);
    gl.uniform2fv(loc.meshPts, positions);
    gl.uniform3fv(loc.meshCols, colors);
    gl.uniform1fv(loc.meshRad, radii);
    gl.bindVertexArray(vao);
    gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);

    for (let i = 0; i < levels.length - 1; i++) {
      drawQuad(downProg, levels[i + 1], levels[i], loc.downOff, loc.downTex);
    }
    for (let i = levels.length - 1; i > 0; i--) {
      drawQuad(upProg, levels[i - 1], levels[i], loc.upOff, loc.upTex);
    }
    drawQuad(presentProg, null, levels[0], loc.presentOff, loc.presentTex);

    animationRef.current = requestAnimationFrame(animate);
  };

  const onResize = () => {
    pendingResize = true;
  };
  window.addEventListener('resize', onResize);
  animate();

  return () => {
    window.removeEventListener('resize', onResize);
    unbind();
    if (animationRef.current) cancelAnimationFrame(animationRef.current);
    disposeLevels();
    gl.deleteBuffer(buffer);
    gl.deleteVertexArray(vao);
    gl.deleteProgram(meshProg);
    gl.deleteProgram(downProg);
    gl.deleteProgram(upProg);
    gl.deleteProgram(presentProg);
  };
};
